/**
 * trackingLadezeitpunkt.ts · Wann Tracking-Skripte laden dürfen
 *
 * WARUM ES DAS GIBT (Perf-Audit 18.09.2026, Punkt 3): Auf den Marketing-Seiten
 * lagen gtag.js (193 KB) und Sentry Session Replay (replay.min.js, ~150 ms
 * Skriptzeit im Labor) auf dem kritischen Pfad, obwohl beide zum ersten Bild
 * nichts beitragen. gtag.js ist dabei gar nicht nötig, um einen Befehl zu
 * BEHALTEN: der Stub aus index.html puffert alles im dataLayer und gtag.js
 * arbeitet die Warteschlange beim Laden in Originalreihenfolge ab. Verzögert
 * werden darf also nur das Skript-Tag, nie ein Befehl.
 *
 * Hier stehen deshalb an EINER Stelle:
 *  - `nachLeerlauf`: „erst, wenn der Browser nichts Wichtigeres zu tun hat“
 *    (nach `load`, dann requestIdleCallback mit Fallback-Timeout).
 *  - Die Pfadlisten, auf denen diese Regel NICHT gilt (`GTAG_SOFORT_PFADE`) bzw.
 *    auf denen Replay überhaupt laufen soll (`REPLAY_PFADE`), jeweils mit
 *    Begründung.
 *  - `pfadPasst` / `sobaldPfadPasst`: Pfadmuster prüfen, auch über SPA-
 *    Navigationen hinweg.
 *
 * Nicht hier: der Consent-Gate, die Bot-/Preview-Guards und die Vorab-Render-
 * Sperre (`nachAktivierung` in prerendering.ts). Die Aufrufer kombinieren.
 */

// ── Pfadmuster ───────────────────────────────────────────────────────────────

/**
 * Ein Muster mit `*` am Ende ist ein Präfix (`/admin*` trifft /admin und
 * /admin/customers), ohne `*` eine exakte Route (`/schnuppertag` trifft NICHT
 * /schnuppertag-success). Der Query-String spielt nie eine Rolle.
 */
export function pfadPasst(pathname: string, muster: readonly string[]): boolean {
  return muster.some((m) => (m.endsWith("*") ? pathname.startsWith(m.slice(0, -1)) : pathname === m));
}

/**
 * Seiten, auf denen gtag.js SOFORT laden muss (nur hinter der Vorab-Render-
 * Sperre), obwohl das den Erstpaint kostet.
 *
 * WARUM: Auf Erfolgs- und Formularseiten feuert eine Google-Ads-Conversion beim
 * Mount bzw. beim Absenden. Der Befehl selbst überlebt im dataLayer, aber
 * SENDEN kann ihn erst gtag.js. Schließt jemand den Tab, bevor das Skript nach
 * Leerlauf geladen wurde, ist die Conversion weg. Genau diese Zahlen steuern
 * das Ads-Budget, deshalb zählt hier die Messung mehr als 200 ms LCP.
 */
export const GTAG_SOFORT_PFADE: readonly string[] = [
  // Erfolgsseiten: Conversion feuert beim Mount, die Seite wird oft sofort geschlossen.
  "/schnuppertag-success",
  "/gassi-success",
  "/payment-success",
  "/booking/success",
  // Schnuppertag-Formular: bezahlter Ads-Traffic landet hier; Remarketing-Hit und
  // Conversion-Kette sollen vom ersten Moment an stehen.
  "/schnuppertag",
  // Wartelisten-Formular mit Ads-Conversion auf DERSELBEN Seite (kein Success-Redirect).
  "/hundetagesstaette",
  // Daycare-Funnel (Check, Interesse, Onboarding): Conversions ohne Erfolgsseite.
  "/daycare/*",
  // Eingeloggter Bereich: der Erstpaint ist dort egal, die Daten laden ohnehin lange.
  "/dashboard*",
  "/admin*",
];

/**
 * Bereiche, in denen Sentry Session Replay geladen wird — nur eingeloggte Flächen.
 *
 * WARUM: Marketing-Seiten sind anonym und der Erstpaint entscheidet dort über
 * Ads-Kosten. Fehler auf diesen Seiten haben damit KEIN Replay mehr, nur
 * Stacktrace und Breadcrumbs; das ist gewollt. Im eingeloggten Bereich (dort
 * stecken die Kundinnen-Bugs wie der RangeError auf /dashboard) bleibt Replay,
 * lädt aber ebenfalls erst nach Leerlauf.
 */
export const REPLAY_PFADE: readonly string[] = [
  "/dashboard*",
  "/admin*",
  "/walker*",
  "/daycare*",
  "/dog/*",
  "/export*",
];

// ── Leerlauf ─────────────────────────────────────────────────────────────────

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

/**
 * Führt `fn` genau einmal aus, sobald die Seite geladen ist UND der Browser
 * Leerlauf meldet.
 *
 * Ablauf: erst `load` abwarten (das erste Bild, die Webfonts und die Route-
 * Chunks sind dann durch), dann `requestIdleCallback`. `spaetestensMs` ist der
 * Timeout des Idle-Callbacks (bzw. der setTimeout-Ersatz in Safari), damit ein
 * dauerbeschäftigter Main-Thread das Laden nicht endlos verschiebt.
 *
 * `loadSicherheitsnetzMs`: Hängt `load` an einer stockenden Fremdressource (ein
 * Bild, ein Widget), wartet Tracking sonst mit. Nach dieser Frist geht es auch
 * ohne `load` weiter. Bewusst großzügig: auf einem langsamen Netz ist auch der
 * Erstpaint spät, und bis dahin soll gtag.js nicht um Bandbreite konkurrieren.
 *
 * `fn` darf nicht werfen; die Aufrufer sind Tracker mit eigener try/catch-Hülle.
 */
export function nachLeerlauf(
  fn: () => void,
  { spaetestensMs = 2000, loadSicherheitsnetzMs = 5000 }: { spaetestensMs?: number; loadSicherheitsnetzMs?: number } = {},
): void {
  if (typeof window === "undefined") return;

  let gestartet = false;
  const start = () => {
    if (gestartet) return;
    gestartet = true;
    fn();
  };

  const imLeerlauf = () => {
    const ric = (window as IdleWindow).requestIdleCallback;
    if (typeof ric === "function") ric(start, { timeout: spaetestensMs });
    else setTimeout(start, spaetestensMs);
  };

  if (document.readyState === "complete") {
    imLeerlauf();
    return;
  }

  let geplant = false;
  const einmalPlanen = () => {
    if (geplant) return;
    geplant = true;
    imLeerlauf();
  };
  window.addEventListener("load", einmalPlanen, { once: true });
  setTimeout(einmalPlanen, loadSicherheitsnetzMs);
}

// ── Pfadwechsel in der SPA ───────────────────────────────────────────────────

type PfadListener = (pathname: string) => void;
const pfadListeners = new Set<PfadListener>();
let historyVerdrahtet = false;

/**
 * React Router navigiert per history.pushState/replaceState; dafür gibt es kein
 * Event. Wir hängen uns einmal an beide Methoden (plus popstate für die
 * Zurück-Taste) und melden den neuen Pfad. Wer das Original ruft, merkt nichts:
 * Rückgabewert und Argumente bleiben unangetastet.
 */
function historyVerdrahten(): void {
  if (historyVerdrahtet || typeof window === "undefined" || typeof history === "undefined") return;
  historyVerdrahtet = true;

  const melden = () => {
    const pathname = window.location.pathname;
    pfadListeners.forEach((l) => {
      try {
        l(pathname);
      } catch {
        /* ein kaputter Listener darf die Navigation nicht stören */
      }
    });
  };

  for (const methode of ["pushState", "replaceState"] as const) {
    const original = history[methode];
    history[methode] = function patchedHistory(this: History, ...args: Parameters<History["pushState"]>) {
      const ergebnis = original.apply(this, args);
      melden();
      return ergebnis;
    };
  }
  window.addEventListener("popstate", melden);
}

/**
 * Meldet jeden history-Aufruf und jedes popstate mit dem dann gültigen Pfad, synchron
 * und bevor React die neue Route rendert. Der Listener prüft selbst, ob sich der Pfad
 * geändert hat (Dialoge rufen pushState ohne neue Adresse). Genutzt vom Web-Tracker
 * (interactions.ts), damit Abschnitts-Zeiten und Scrolltiefe am alten Pfad enden.
 */
export function beiPfadwechsel(fn: PfadListener): void {
  if (typeof window === "undefined") return;
  historyVerdrahten();
  pfadListeners.add(fn);
}

/**
 * Ruft `fn` genau einmal, sobald der aktuelle Pfad auf eines der Muster passt,
 * jetzt sofort oder nach einer späteren SPA-Navigation (z. B. /login → /dashboard).
 * Gibt eine Abbruch-Funktion zurück.
 */
export function sobaldPfadPasst(muster: readonly string[], fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  if (pfadPasst(window.location.pathname, muster)) {
    fn();
    return () => {};
  }

  historyVerdrahten();
  const listener: PfadListener = (pathname) => {
    if (!pfadPasst(pathname, muster)) return;
    pfadListeners.delete(listener);
    fn();
  };
  pfadListeners.add(listener);
  return () => {
    pfadListeners.delete(listener);
  };
}
